import React, { useEffect, useMemo, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { ErrorMessage } from '@hookform/error-message'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

import { addEditProduct, openModal } from 'store/actions/cart'

import { ValidationError } from 'components/ValidationError'
import { CurrencyValueInput, InputField, NumberInput } from 'components/Input'

import { useValidationSchema } from 'components/ProductContainer/validation'

import { cartProductsSelector } from 'store/selectors/cart'
import {
    Form,
    FormTitle,
    FormColumn,
    FormSwitchRow,
    FormBody,
    FormContent,
    ProductFormItem,
    ErrorMessageContainer,
    FormFields,
    FormHeader,
    InfoSpacer,
    Button,
    Image,
    Label,
    PatSwitch,
    FormHeaderLabels,
    ButtonWrap
} from './DetailProductForm.style'

import { colors } from 'styles/colors'
import PatTooltipModal from 'components/SwitchButton/tooltip/PatTooltipModal'
import { closePatModal } from 'store/actions/home'
import { homePatModalSelector } from 'store/selectors/home'
import useProductImage from 'hooks/useProductImage'

const getDefaultGroups = (groups = []) => {
    const retGroups = {}
    if (groups && groups.length > 0) {
        groups.forEach((group) => {
            retGroups[group.type] = {
                cardQuantity: group.qnt,
                monthlyValue: group.value
            }
        })
    }
    return retGroups
}

const getDefaultValues = (values = {}) => ({
    cardQuantity: values?.qnt ?? '',
    monthlyValue: values?.value ?? '',
    groups: getDefaultGroups(values?.groups?.items),
    isPat: values?.isPat ?? false
})

const DetailProductForm = ({
    cartProducts,
    product,
    addEditProduct,
    openCartMenu,
    patModal,
    closePatModal
}) => {
    const schema = useValidationSchema()

    const cartProductInfo = cartProducts.find((cartProduct) => {
        return +product.id === +cartProduct.id
    })

    const lastCartProcuctInfo = useRef(cartProductInfo)

    const image = useProductImage(product)

    const formMethods = useForm({
        defaultValues: getDefaultValues(cartProductInfo),
        resolver: yupResolver(schema),
        context: {
            minPerCard: 1,
            maxPerCard: 9999,
            minPerValue: product.minValue ?? 1,
            maxPerValue: product.maxValue ?? 9999,
            product
        }
    })

    const { handleSubmit, watch, trigger, formState, reset, clearErrors } =
        formMethods

    const isPatChecked = watch('isPat')

    useEffect(() => {
        if (
            JSON.stringify(lastCartProcuctInfo.current) !==
            JSON.stringify(cartProductInfo)
        ) {
            reset(getDefaultValues(cartProductInfo))
            setTimeout(() => {
                clearErrors()
            }, 0)
        }
        lastCartProcuctInfo.current = cartProductInfo
    }, [cartProductInfo, reset, clearErrors])

    useEffect(() => {
        if (formState.isSubmitted) {
            trigger()
            trigger('sumCardQuantity')
        }
    }, [isPatChecked, trigger, formState.isSubmitted])

    const onSubmit = (data) => {
        const groupsData = {
            items: Object.keys(data.groups).map((key) => ({
                type: key,
                value: data.groups[key].monthlyValue ?? '',
                qnt: data.groups[key].cardQuantity ?? '',
                total: data.groups[key].total ?? 0.0,
                adminstrationTax: data.groups[key].adminstrationTax ?? 0.0,
                feeType: data.groups[key].feeType ?? 'PERCENTAGE',
                totalFee: data.groups[key].totalFee ?? 0.0
            })),
            total: product.groups.total ?? 0.0,
            totalFee: product.groups.totalFee ?? 0.0
        }

        addEditProduct({
            id: product.id,
            qnt: data.cardQuantity,
            value: data.monthlyValue,
            groups: groupsData,
            isPat: data.isPat,
            loading: false
        })
        openCartMenu()
    }

    const groups = useMemo(
        () => product.groups.filter((g) => g.isPat.includes(isPatChecked)),
        [isPatChecked, product.groups]
    )
    const onSwitchChange = (_, onChange) => {
        onChange()
    }

    return (
        <Form>
            <FormProvider {...formMethods}>
                <form
                    noValidate
                    autoComplete="off"
                    onSubmit={handleSubmit(onSubmit)}
                >
                    <FormContent
                        $color={product.color}
                        $backgroundColor={product.backgroundFormColor}
                    >
                        <FormHeader>
                            <Image
                                id={product.id}
                                src={image}
                                alt={`Produto ${product.name}`}
                            />
                            <FormTitle $color={product.color}>
                                Defina a quantidade de cartões {product.name}{' '}
                                <span className="mobile-only">
                                    e o valor mensal por cartão:
                                </span>
                            </FormTitle>
                            <FormFields>
                                <FormColumn className="card-quantity">
                                    <Label
                                        $color={product.buttonBgColor}
                                        htmlFor="cardQuantity"
                                    >
                                        Qnt. de cartões
                                    </Label>
                                    <InputField
                                        as={NumberInput}
                                        errorColor={product.errorColor}
                                        tooltipErrors
                                        id="cardQuantity"
                                        name="cardQuantity"
                                        type="tel"
                                        aria-label={`Quantidade de cartões ${product.name}`}
                                        mask={[
                                            { mask: Number, min: 0, max: 50000 }
                                        ]}
                                        unmask="typed"
                                    />
                                </FormColumn>
                                <FormColumn className="card-value">
                                    <Label
                                        $color={product.buttonBgColor}
                                        htmlFor="monthlyValue"
                                    >
                                        Valor mensal por cartão
                                    </Label>
                                    <InputField
                                        as={CurrencyValueInput}
                                        errorColor={product.errorColor}
                                        tooltipErrors
                                        decimalSeparator=","
                                        thousandSeparator="."
                                        prefix="R$ "
                                        inputType="tel"
                                        maxLength={13}
                                        id="monthlyValue"
                                        name="monthlyValue"
                                        type="tel"
                                        aria-label="Valor mensal por cartão"
                                        defaultValue={
                                            watch('monthlyValue') ?? ''
                                        }
                                    />
                                </FormColumn>
                            </FormFields>
                        </FormHeader>
                        <FormBody $color={product.colorAccent}>
                            <FormSwitchRow>
                                <span>
                                    Escolha os benefícios e distribua de acordo
                                    com a quantidade de cartões informada acima:
                                </span>
                                <PatSwitch
                                    name="isPat"
                                    onChange={onSwitchChange}
                                    defaultValue={isPatChecked}
                                    color={colors.black}
                                    $backgroundColor={
                                        product.patSwBackgroundColor
                                    }
                                    $colorAccent={product.patSwColorAccent}
                                    $color={product.patColor}
                                />
                                {patModal && (
                                    <PatTooltipModal onClose={closePatModal} />
                                )}
                            </FormSwitchRow>
                            <FormHeaderLabels>
                                <InfoSpacer />
                                <Label
                                    whiteSpace="wrap"
                                    className="card-quantity"
                                    $color={product.labelColor}
                                >
                                    Qtde de colaboradores
                                </Label>
                                <Label
                                    whiteSpace="wrap"
                                    className="card-value"
                                    $color={product.labelColor}
                                >
                                    Valor mensal por colaborador
                                </Label>
                            </FormHeaderLabels>
                            {groups.map((group, index) => (
                                <ProductFormItem
                                    key={group.type}
                                    product={product}
                                    group={group}
                                    index={index}
                                />
                            ))}
                            <ErrorMessageContainer>
                                <ErrorMessage
                                    name="sumCardQuantity"
                                    render={({ message }) => (
                                        <ValidationError
                                            color={product.errorColor}
                                        >
                                            {message}
                                        </ValidationError>
                                    )}
                                />
                            </ErrorMessageContainer>
                        </FormBody>
                        <ButtonWrap>
                            <Button
                                id={`btnAdd${product.id}Cart`}
                                type="submit"
                                kind="add"
                                $background={product.buttonBgColor}
                                $color={product.buttonColor}
                            >
                                {cartProductInfo
                                    ? 'Atualizar cesta'
                                    : 'Ver quanto custa'}
                            </Button>
                        </ButtonWrap>
                    </FormContent>
                </form>
            </FormProvider>
        </Form>
    )
}

const mapStateToProps = (state) => ({
    cartProducts: cartProductsSelector(state),
    patModal: homePatModalSelector(state)
})

const mapDispatchToProps = (dispatch) => ({
    addEditProduct: (product) => dispatch(addEditProduct(product)),
    openCartMenu: () => dispatch(openModal()),
    closePatModal: () => dispatch(closePatModal())
})

export default withRouter(
    connect(mapStateToProps, mapDispatchToProps)(DetailProductForm)
)
